// Centralised, human-friendly date formatting for the whole site.
// Renders dates like "June 6th 2026" (optionally "Saturday, June 6th 2026").
// Accepts "YYYY-MM-DD", full ISO timestamps, or Date objects.

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export function formatDate(value, { fallback = "—", weekday = false, comma = false, time = false } = {}) {
    if (!value) return fallback;
    let d;
    if (value instanceof Date) {
        d = value;
    } else if (typeof value === "string") {
        // For date-only / ISO prefixes, parse the calendar date locally so the
        // displayed day never shifts due to timezone conversion. When a time is
        // requested we parse the full timestamp instead (otherwise it's lost).
        const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        d = (m && !time) ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
    } else {
        d = new Date(value);
    }
    if (isNaN(d.getTime())) return typeof value === "string" ? value : fallback;
    const base = `${MONTHS[d.getMonth()]} ${ordinal(d.getDate())}${comma ? "," : ""} ${d.getFullYear()}`;
    let out = weekday ? `${WEEKDAYS[d.getDay()]}, ${base}` : base;
    if (time) {
        let h = d.getHours();
        const min = String(d.getMinutes()).padStart(2, "0");
        const ap = h >= 12 ? "PM" : "AM";
        h = h % 12 || 12;
        out += ` · ${h}:${min} ${ap}`;
    }
    return out;
}

// Flexible parse of a free-typed date. Accepts:
//   "2026-02-06" / "2026/02/06" (YYYY MM DD)
//   "February 6th, 2026" / "Feb 6 2026" / "February 6 2026"
// Returns a Date (local, no TZ shift for the YYYY-x-x forms) or null.
export function parseFlexibleDate(value) {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    const s = String(value).trim();
    const m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (m) {
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return isNaN(d.getTime()) ? null : d;
    }
    // Strip ordinal suffixes ("6th" -> "6") so Date can parse the prose form.
    const cleaned = s.replace(/(\d+)(st|nd|rd|th)/gi, "$1");
    const d2 = new Date(cleaned);
    return isNaN(d2.getTime()) ? null : d2;
}

// Pretty-print any flexible date input to "February 6th 2026"; if it can't be
// parsed, return the original text (so admins can type free-form if needed).
export function formatFlexibleDate(value, { fallback = "—" } = {}) {
    if (!value) return fallback;
    const d = parseFlexibleDate(value);
    return d ? formatDate(d) : String(value);
}

// Build a specific retirement/archive status string from a player record:
//   "Retired after 2025 season"
//   "Retired during 2025 season on February 6th 2026"
// Falls back to a legacy note / date when the structured fields aren't set.
export function formatRetirement(p, { fallback = "" } = {}) {
    if (!p) return fallback;
    const type = p.retirement_type || "";
    if (type === "cut") return `Cut${p.cut_date ? ` ${formatFlexibleDate(p.cut_date)}` : ""} · Inactive`;
    const season = String(p.retirement_season || "").trim();
    const date = p.retirement_date || "";
    if (type === "after" && season) return `Retired after ${season} season`;
    if (type === "during" && season) {
        return `Retired during ${season} season${date ? ` on ${formatFlexibleDate(date)}` : ""}`;
    }
    if (p.retirement_note) return p.retirement_note;
    if (date) return `Retired ${formatFlexibleDate(date)}`;
    return fallback;
}

// Compact "Jun 30, 1999" — used for tight mobile layouts.
export function formatShortDate(value, { fallback = "—" } = {}) {
    if (!value) return fallback;
    const d = parseFlexibleDate(value);
    if (!d) return String(value);
    return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`;
}

// Month + year only, e.g. "February 2026". Accepts "YYYY-MM" or "YYYY-MM-DD".
export function formatMonthYear(value, { fallback = "—" } = {}) {
    if (!value) return fallback;
    const m = String(value).match(/^(\d{4})-(\d{2})/);
    if (!m) return value;
    return `${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}
